/**
 * Multi-Cognito auth manager for AgentFront.
 * Handles separate Cognito pools for Confluence (IdToken) and Jira (AccessToken).
 * Credentials are read from secureStorage — never hardcoded.
 */

import { secureStorage, KEYS } from './secureStorage'
import { config } from './config'

// Cognito configs — URLs are public, no secrets here
const COGNITO_ENDPOINT = 'https://cognito-idp.us-east-1.amazonaws.com/'
const CONFLUENCE_CONFIG = {
  clientId: '60ps0o9uijosjnvim9uloiiggm',
  tokenField: 'IdToken' as const,
}

// Jira MCP (dev branch): uses IdToken because tenant_id is extracted
// from JWT claims (custom:tenant_id) server-side
const JIRA_CONFIG = {
  clientId: '60ps0o9uijosjnvim9uloiiggm',
  tokenField: 'IdToken' as const,
}
interface CognitoAuthResult {
  IdToken: string
  AccessToken: string
  RefreshToken: string
  ExpiresIn: number
}
async function authenticateCognito(
  clientId: string,
  username: string,
  password: string,
): Promise<CognitoAuthResult> {
  const response = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: clientId,
      AuthParameters: {
        USERNAME: username,
        PASSWORD: password,
      },
    }),
  })
  const data = await response.json()
  if (data.__type || !data.AuthenticationResult) {
    throw new Error(data.message || 'Authentication failed')
  }
  return data.AuthenticationResult
}
export type McpService = 'confluence' | 'jira'

// Auto-seed credentials from config ONLY if no user is logged in
// AND no credentials exist in secureStorage.
// When a user logs in, App.tsx handles setting the correct MCP credentials.
function ensureCredentials() {
  // Check if a user is already logged in — if so, DON'T overwrite with defaults
  try {
    const activeUser = localStorage.getItem('vsm_active_user')
    if (activeUser) {
      console.log(
        '[McpAuth] User session found — skipping default credential seeding',
      )
      return
    }
  } catch {}

  // Only seed defaults when NO user is logged in (e.g., first visit before login)
  // These will be overwritten by App.tsx when a user logs in
  if (
    !secureStorage.has(KEYS.JIRA_USER) &&
    !secureStorage.has(KEYS.CONFLUENCE_USER)
  ) {
    const confCreds = config.mcpCredentials.confluence
    if (confCreds.username && confCreds.password) {
      secureStorage.set(KEYS.CONFLUENCE_USER, confCreds.username)
      secureStorage.set(KEYS.CONFLUENCE_PASS, confCreds.password)
      console.log(
        '[McpAuth] Auto-seeded Confluence credentials (no user logged in)',
      )
    }
    const jiraCreds = config.mcpCredentials.jira
    if (jiraCreds.username && jiraCreds.password) {
      secureStorage.set(KEYS.JIRA_USER, jiraCreds.username)
      secureStorage.set(KEYS.JIRA_PASS, jiraCreds.password)
      console.log('[McpAuth] Auto-seeded Jira credentials (no user logged in)')
    }
  }
}

// Run on module load
ensureCredentials()

// Pre-warm tokens in background — but ONLY if no user session exists
// (if a user is logged in, App.tsx will set their credentials and tokens will be fetched on demand)
function preWarmTokens() {
  try {
    const activeUser = localStorage.getItem('vsm_active_user')
    if (activeUser) {
      console.log(
        '[McpAuth] User session found — skipping pre-warm (App.tsx will handle credentials)',
      )
      return
    }
  } catch {}
  const services: McpService[] = ['confluence', 'jira']
  services.forEach((service) => {
    const userKey =
      service === 'confluence' ? KEYS.CONFLUENCE_USER : KEYS.JIRA_USER
    if (secureStorage.has(userKey)) {
      mcpAuth
        .getToken(service)
        .then(() => {
          console.log(`[McpAuth] Pre-warmed ${service} token ✓`)
        })
        .catch((err) => {
          console.warn(`[McpAuth] Pre-warm ${service} failed:`, err.message)
        })
    }
  })
}

/**
 * Authenticate a user against Cognito and store tokens + credentials.
 * This is the REAL login — called from LoginPage.
 * Returns the IdToken on success.
 */
export async function loginWithCognito(
  username: string,
  password: string,
): Promise<{
  idToken: string
  accessToken: string
  expiresIn: number
}> {
  const clientId = JIRA_CONFIG.clientId // Same pool for everything
  const result = await authenticateCognito(clientId, username, password)

  // Store tokens for both Jira and Confluence (same pool)
  const expiry = Date.now() + result.ExpiresIn * 1000 - 300000
  secureStorage.set(KEYS.JIRA_TOKEN, result.IdToken)
  secureStorage.set(KEYS.JIRA_TOKEN_EXPIRY, expiry.toString())
  secureStorage.set(KEYS.CONFLUENCE_TOKEN, result.IdToken)
  secureStorage.set(KEYS.CONFLUENCE_TOKEN_EXPIRY, expiry.toString())

  // Store credentials for auto-refresh
  secureStorage.set(KEYS.JIRA_USER, username)
  secureStorage.set(KEYS.JIRA_PASS, password)
  secureStorage.set(KEYS.CONFLUENCE_USER, username)
  secureStorage.set(KEYS.CONFLUENCE_PASS, password)
  console.log('[McpAuth] Login successful — tokens and credentials stored')
  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    expiresIn: result.ExpiresIn,
  }
}
export const mcpAuth = {
  /** Check if credentials are configured for a service */
  isConfigured(service: McpService): boolean {
    if (service === 'confluence') {
      return (
        secureStorage.has(KEYS.CONFLUENCE_USER) &&
        secureStorage.has(KEYS.CONFLUENCE_PASS)
      )
    }
    return (
      secureStorage.has(KEYS.JIRA_USER) && secureStorage.has(KEYS.JIRA_PASS)
    )
  },
  /** Save credentials for a service (called from Settings UI) */
  saveCredentials(service: McpService, username: string, password: string) {
    if (service === 'confluence') {
      secureStorage.set(KEYS.CONFLUENCE_USER, username)
      secureStorage.set(KEYS.CONFLUENCE_PASS, password)
      // Clear cached token so next call re-authenticates
      secureStorage.remove(KEYS.CONFLUENCE_TOKEN)
      secureStorage.remove(KEYS.CONFLUENCE_TOKEN_EXPIRY)
    } else {
      secureStorage.set(KEYS.JIRA_USER, username)
      secureStorage.set(KEYS.JIRA_PASS, password)
      secureStorage.remove(KEYS.JIRA_TOKEN)
      secureStorage.remove(KEYS.JIRA_TOKEN_EXPIRY)
    }
  },
  /** Get a valid token for a service. Auto-refreshes if expired. */
  async getToken(service: McpService): Promise<string> {
    const tokenKey =
      service === 'confluence' ? KEYS.CONFLUENCE_TOKEN : KEYS.JIRA_TOKEN
    const expiryKey =
      service === 'confluence'
        ? KEYS.CONFLUENCE_TOKEN_EXPIRY
        : KEYS.JIRA_TOKEN_EXPIRY
    const userKey =
      service === 'confluence' ? KEYS.CONFLUENCE_USER : KEYS.JIRA_USER
    const passKey =
      service === 'confluence' ? KEYS.CONFLUENCE_PASS : KEYS.JIRA_PASS
    const cognitoConfig =
      service === 'confluence' ? CONFLUENCE_CONFIG : JIRA_CONFIG

    // Check cached token
    const cachedToken = secureStorage.get(tokenKey)
    const cachedExpiry = secureStorage.get(expiryKey)
    if (cachedToken && cachedExpiry && Date.now() < parseInt(cachedExpiry)) {
      return cachedToken
    }

    // Need to authenticate
    const username = secureStorage.get(userKey)
    const password = secureStorage.get(passKey)
    if (!username || !password) {
      throw new Error(
        `Credenciales de ${service === 'confluence' ? 'Confluence' : 'Jira'} no configuradas. ` +
          'Configuralas en Settings → AgentFront.',
      )
    }
    console.log(`[McpAuth] Authenticating ${service}...`)
    const result = await authenticateCognito(
      cognitoConfig.clientId,
      username,
      password,
    )
    const token = result[cognitoConfig.tokenField]
    const expiry = Date.now() + result.ExpiresIn * 1000 - 300000 // 5 min buffer

    secureStorage.set(tokenKey, token)
    secureStorage.set(expiryKey, expiry.toString())
    return token
  },
  /** Clear all cached tokens (force re-auth) */
  clearTokens() {
    secureStorage.remove(KEYS.CONFLUENCE_TOKEN)
    secureStorage.remove(KEYS.CONFLUENCE_TOKEN_EXPIRY)
    secureStorage.remove(KEYS.JIRA_TOKEN)
    secureStorage.remove(KEYS.JIRA_TOKEN_EXPIRY)
  },
  /** Clear cached token for a specific service (used by auto-retry) */
  clearTokenForService(service: McpService) {
    if (service === 'confluence') {
      secureStorage.remove(KEYS.CONFLUENCE_TOKEN)
      secureStorage.remove(KEYS.CONFLUENCE_TOKEN_EXPIRY)
    } else {
      secureStorage.remove(KEYS.JIRA_TOKEN)
      secureStorage.remove(KEYS.JIRA_TOKEN_EXPIRY)
    }
    console.log(`[McpAuth] Cleared ${service} token for auto-refresh`)
  },
  /** Get connection status for both services */
  getStatus(): {
    confluence: 'connected' | 'configured' | 'not_configured'
    jira: 'connected' | 'configured' | 'not_configured'
  } {
    const confluenceToken = secureStorage.get(KEYS.CONFLUENCE_TOKEN)
    const confluenceExpiry = secureStorage.get(KEYS.CONFLUENCE_TOKEN_EXPIRY)
    const jiraToken = secureStorage.get(KEYS.JIRA_TOKEN)
    const jiraExpiry = secureStorage.get(KEYS.JIRA_TOKEN_EXPIRY)
    return {
      confluence:
        confluenceToken &&
        confluenceExpiry &&
        Date.now() < parseInt(confluenceExpiry)
          ? 'connected'
          : this.isConfigured('confluence')
            ? 'configured'
            : 'not_configured',
      jira:
        jiraToken && jiraExpiry && Date.now() < parseInt(jiraExpiry)
          ? 'connected'
          : this.isConfigured('jira')
            ? 'configured'
            : 'not_configured',
    }
  },
}

// Pre-warm after mcpAuth is defined
preWarmTokens()
